import { c as _c } from "react/compiler-runtime";
import { NodeSelection, Selection } from '@tiptap/pm/state';
import type { NodeViewProps } from '@tiptap/react';
import { NodeViewWrapper, useEditorState } from '@tiptap/react';
import type { ComponentType, MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { type BlockFenceRegistry, isBlockFenceKind, renderBlockFence } from '../block-fence-registry';
const InvalidBlock = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="rounded-lg border border-error bg-error px-6 py-5 text-body-2 text-error">This block contains invalid data. Edit its fenced JSON to repair it.</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// Why a fence in the vocabulary's namespace is kept as text instead of becoming a block.
export type RawFenceReason = 'unclosed' | 'indented' | 'extraWords' | 'unknownKind' | 'invalidConfig';
export type RawFenceNoticeProps = {
  reason: RawFenceReason | null;
  editable: boolean;
  onDelete: () => void;
};
export type UpgradeNoticeProps = {
  // False when the block's config would not pass the newer vocabulary's parser.
  convertible: boolean;
  editable: boolean;
  onConvert: () => void;
};

// A vocabulary the runtime no longer compiles, and the node that replaces it.
export type BlockFenceUpgrade = {
  nodeName: string;
  registry: BlockFenceRegistry;
  Notice: ComponentType<UpgradeNoticeProps>;
};
export type BlockFenceWrapperProps = {
  children: ReactNode;
  nodeView: NodeViewProps;
  justInserted: boolean;
};
export type BlockFenceNodeViewOptions = {
  Wrapper?: ComponentType<BlockFenceWrapperProps>;
  registry: BlockFenceRegistry;
  dataPrefix: string;
  // A single-line config makes a hand-edit of the raw markdown unreadable, but turning it on for an
  // existing vocabulary would rewrite stored fences on the next edit — so each caller opts in.
  prettyPrintConfig: boolean;
  RawFenceNotice?: ComponentType<RawFenceNoticeProps>;
  upgrade?: BlockFenceUpgrade;
};
const BlockFenceNodeView = (props: NodeViewProps) => {
  const {
    node,
    editor,
    extension,
    getPos,
    updateAttributes,
    deleteNode
  } = props;
  const {
    registry,
    dataPrefix,
    prettyPrintConfig,
    RawFenceNotice,
    upgrade,
    Wrapper
  } = extension.options as BlockFenceNodeViewOptions;
  const kind = node.attrs.kind as string;
  const rawConfig = node.attrs.config as string;

  // Tiptap React node views don't re-render on editor.setEditable(...); subscribe so a block
  // becomes editable/read-only in step with the surrounding prose (e.g. after a restore).
  const isEditable = useEditorState({
    editor,
    selector: ({
      editor: e
    }) => e.isEditable
  });

  // Read once, at mount: a block that appears while the editor has focus was just inserted by hand,
  // which a block loaded with the document was not.
  const [justInserted] = useState(() => editor.isFocused);
  const handleChange = (next: unknown) => {
    updateAttributes({
      config: prettyPrintConfig ? JSON.stringify(next, null, 2) : JSON.stringify(next)
    });
  };

  // The header bar selects the whole block; a press anywhere else is someone working in it, and it has
  // to clear the selection a Backspace or a header press left — ProseMirror never sees these presses,
  // since the node view swallows them so the fields can type. Capture phase, so it beats a field's focus.
  const handlePress = (event: MouseEvent<HTMLElement>) => {
    const pos = getPos();
    if (pos === undefined) return;
    const target = event.target as HTMLElement;
    const {
      selection
    } = editor.state;
    if (target.closest('[data-block-header]') && !target.closest('button')) {
      editor.chain().setNodeSelection(pos).focus(undefined, {
        scrollIntoView: false
      }).run();
      return;
    }

    // By position, not node identity: the prop is not always the same object the selection points at.
    if (!(selection instanceof NodeSelection) || selection.from !== pos) return;
    const caret = Selection.findFrom(editor.state.doc.resolve(selection.to), 1, true) ?? Selection.findFrom(editor.state.doc.resolve(selection.from), -1, true);
    if (caret) editor.view.dispatch(editor.state.tr.setSelection(caret).setMeta('addToHistory', false));
  };

  // Deleting a block on its own leaves ProseMirror to pick the next selection, and next to a block that
  // is a node selection on the neighbour — so removing one block appears to select the one after it.
  // Land the caret in text instead.
  const removeBlock = () => {
    const pos_0 = getPos();
    const block = pos_0 === undefined ? null : editor.state.doc.nodeAt(pos_0);
    if (pos_0 === undefined || !block) {
      deleteNode();
      return;
    }
    const tr = editor.state.tr.delete(pos_0, pos_0 + block.nodeSize);
    const at = tr.doc.resolve(Math.min(pos_0, tr.doc.content.size));
    const caret_0 = Selection.findFrom(at, 1, true) ?? Selection.findFrom(at, -1, true);
    if (caret_0) tr.setSelection(caret_0);
    editor.view.dispatch(tr);
  };
  if (typeof node.attrs.rawFence === 'string') {
    const content = <>
        {RawFenceNotice && <RawFenceNotice editable={isEditable} onDelete={removeBlock} reason={node.attrs.rawReason as RawFenceReason | null ?? null} />}
        <pre className="whitespace-pre-wrap">
          <code>{node.attrs.rawFence}</code>
        </pre>
      </>;
    return <NodeViewWrapper contentEditable={false}>
        {Wrapper ? <Wrapper nodeView={props} justInserted={justInserted}>
            {content}
          </Wrapper> : content}
      </NodeViewWrapper>;
  }
  const convert = () => {
    const pos_1 = getPos();
    const type = upgrade && editor.schema.nodes[upgrade.nodeName];
    if (pos_1 === undefined || !type) return;
    editor.view.dispatch(editor.state.tr.replaceWith(pos_1, pos_1 + node.nodeSize, type.create({
      kind,
      config: rawConfig
    })));
  };
  const exitBlock = () => {
    const pos_2 = getPos();
    if (pos_2 === undefined) return;
    const after = editor.state.doc.resolve(Math.min(pos_2 + node.nodeSize, editor.state.doc.content.size));
    const caret_1 = Selection.findFrom(after, 1, true) ?? Selection.findFrom(editor.state.doc.resolve(pos_2), -1, true);
    if (caret_1) {
      editor.chain().setTextSelection(caret_1.from).focus(undefined, {
        scrollIntoView: false
      }).run();
    } else {
      editor.chain().setNodeSelection(pos_2).createParagraphNear().focus().run();
    }
  };
  const rendered = renderBlockFence(registry, kind, rawConfig, {
    editable: isEditable,
    onChange: handleChange,
    onDelete: removeBlock,
    onExit: exitBlock,
    justInserted
  });
  const content_0 = <>
      {upgrade && rendered && <upgrade.Notice convertible={isBlockFenceKind(upgrade.registry, kind) && upgrade.registry[kind].parse(rawConfig) !== null} editable={isEditable} onConvert={convert} />}
      {rendered ?? <InvalidBlock />}
    </>;
  return <NodeViewWrapper className="group/block relative my-5 w-full cursor-default rounded-lg" contentEditable={false} onMouseDownCapture={handlePress} {...{
    [`${dataPrefix}-kind`]: kind
  }}>
      {/* A schema-valid block can still throw while rendering (d3 math, Intl formatting). Without
          this boundary that throw hits the app-root boundary and whites out the whole page,
          locking the user out of the editor they'd use to repair the block. resetKeys retries on edit. */}
      <ErrorBoundary fallback={<InvalidBlock />} resetKeys={[kind, rawConfig]} onError={error => console.error('[BlockFenceNodeView] block render error:', kind, error)}>
        {Wrapper ? <Wrapper nodeView={props} justInserted={justInserted}>
            {content_0}
          </Wrapper> : content_0}
      </ErrorBoundary>
    </NodeViewWrapper>;
};
export default BlockFenceNodeView;